<!-- props: [ "placeholder", "hintMessage", "status", "type", "label",
"passwordRevealToggle", "clearSearch", "forgotPassword", "validation", ],


pass in status to get desired input color,
type is a text for the inputType,
label is a text for the label of the input,
passwordRevealToggle is a boolean to show or hide the eye icon for toggling the input type between password and text, 
clearSearch is a boolean to show or hide the 'clear text' button which clears the input value,
forgotPassword is a boolean to show the forgot password button,
validation is a boolean to show or hide the password validation rules   .

to get desired input width, in the file where you are using the input wrap the button component in a div and apply a width to that wrapper div 
eg <div classname="w-[10%]"><EpumpInput/></div>

for inputs with icon pass in the icon as a slot eg: <EpumpInputVue><img src="@/assets/svg/lock.svg"/></EpumpInputVue>

example of what the input component might look like in your file:

<EpumpInputVue
:label="'Password'"
:passwordRevealToggle="true"
:clearSearch="false"
:placeholder="'Password'"
:status="'active'"
:type="'password'"
:forgotPassword="false"
:validation="false"
><img src="@/assets/svg/lock.svg"
/></EpumpInputVue> -->

<template>
  <div>
    <div v-if="label" class="flex items-center mb-2 gap-x-2">
      <label class="font-medium text-base leading-5 text-[#000000]">
        {{ label }}
        <span class="text-xl text-red-600" v-if="required">*</span>
      </label>
      <slot name="addedLabel"></slot>
    </div>

    <div
      class="flex items-center justify-between w-full px-4 rounded-lg"
      :class="`${statusStyle.wrapperStyle} ${disabled && 'cursor-not-allowed'}`"
    >
      <div class="w-[90%] flex gap-4 items-center">
        <slot></slot>

        <!-- TEXTAREA HANDLING -->
        <div v-if="type === 'textarea'" class="w-full">
          <textarea
            :name="name"
            :value="modelValue"
            @input="updateValue"
            ref="textarea"
            :maxlength="maxLength"
            :placeholder="placeholder"
            :disabled="disabled"
            :class="`${statusStyle.inputStyle} ${
              disabled && 'cursor-not-allowed opacity-70'
            }`"
            class="w-full py-3 overflow-hidden text-sm font-normal leading-6 outline-none resize-none text-inputGrey-900 placeholder:text-sm placeholder:font-normal placeholder:leading-6"
            :rows="rows"
          ></textarea>

          <!-- CHARACTER COUNTER -->
          <div
            v-if="showCounter && maxLength"
            class="mb-1 text-xs text-right text-gray-500 font-sfProDisplay"
          >
            {{ (modelValue || "").length }} / {{ maxLength }}
          </div>
        </div>

        <!-- NORMAL INPUT -->
        <input
          v-else
          :name="name"
          :required="required"
          :type="inputType"
          :value="modelValue"
          :min="minVal"
          :max="maxVal"
          @input="updateValue"
          :class="`${statusStyle.inputStyle} ${
            disabled && 'cursor-not-allowed opacity-70'
          }`"
          class="w-[100%] text-inputGrey-900 font-normal text-sm outline-none placeholder:font-normal placeholder:text-sm placeholder:leading-6 py-3"
          :placeholder="placeholder"
          :disabled="disabled"
          :autocomplete="autoComplete"
          :pattern="isPin ? '[0-9]*' : null"
        />
      </div>

      <img
        v-if="passwordRevealToggle && !disabled"
        @click="toggleView"
        :src="currentIcon"
        class="cursor-pointer"
      />

      <img
        v-if="disabled"
        src="@/assets/svg/lock.svg"
        alt=""
        class="cursor-not-allowed size-4 opacity-40"
      />

      <p
        v-if="clearSearch"
        @click="clearInput"
        class="text-sm font-medium text-red-400 cursor-pointer"
      >
        clear
      </p>
    </div>

    <div class="flex justify-between">
      <p class="text-[#D44333] font-normal text-sm leading-5 mt-1">
        {{ isEmail() ? emailValError : hintMessage }}
      </p>
      <p
        v-if="forgotPassword"
        class="text-[#595959] font-medium text-sm mt-1 flex justify-end cursor-pointer"
        @click="goToPage"
      >
        Forgot password?
      </p>
    </div>

    <div v-if="validation" class="flex flex-col gap-2 mt-2">
      <div
        class="flex gap-3 align-items-center"
        v-for="(rule, index) in validationRuleList"
        :key="index"
      >
        <img :src="rule.image" alt="validation" />
        <p
          :class="{ 'text-green-500': rule.validated }"
          class="text-sm font-normal leading-5 text-inputGrey-900"
        >
          {{ rule.title }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import eye from "@/assets/svg/eye.svg";
import crossedEye from "@/assets/svg/crossedEye.svg";
import negativeEye from "@/assets/svg/negativeEye.svg";
import sun from "@/assets/svg/sun.svg";
import check from "@/assets/svg/check.svg";
import positiveEye from "@/assets/svg/positiveEye.svg";
import moment from "moment";
import { formatNumber } from "@/core/helpers/utility";

export default {
  props: {
    // modelValue: this?.type == "number" ? Number : String,
    modelValue: Number | String, // For v-model binding
    required: {
      type: Boolean,
      default: false,
      required: false,
    },
    placeholder: String,
    name: String,
    min: {
      type: [String, Number],
      required: false,
      default: 1,
    },
    max: {
      type: String,
      required: false,
    },
    hintMessage: {
      type: String,
      required: false,
    },
    status: String,
    type: {
      type: String,
      default: "text",
    },
    label: {
      type: String,
      required: false,
    },
    passwordRevealToggle: {
      type: Boolean,
      required: false,
    },
    clearSearch: {
      type: Boolean,
      required: false,
    },
    forgotPassword: {
      type: Boolean,
      required: false,
    },
    validation: {
      type: Boolean,
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    autoComplete: {
      type: String,
      required: false,
      default: "",
    },
    isPin: {
      type: Boolean,
      required: false,
      default: false,
    },
    rows: {
      type: Number,
      default: 2,
    },
    maxLength: {
      type: Number,
      default: null,
    },
    showCounter: {
      type: Boolean,
      default: false,
    },
    autoResize: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      formatNumber,
      emailValError: null,
      userIcons: {
        eye: eye,
        crossedEye: crossedEye,
        negativeEye: negativeEye,
        positiveEye: positiveEye,
      },
      validationImages: {
        sun: sun,
        check: check,
      },
      inputType: this.type,
      currentIcon: this.userIcons?.eye,
      statusStyle: {
        wrapperStyle: "py-3",
        inputStyle: "placeholder:text-inputGrey-900",
      },
      validationRuleList: [
        {
          title: "Minimum contain 8 characters",
          validated: false,
          image: this.validationImages?.sun,
        },
        {
          title: "Must contain uppercase",
          validated: false,
          image: this.validationImages?.sun,
        },
        {
          title: "Must contain number",
          validated: false,
          image: this.validationImages?.sun,
        },
        {
          title: "Must contain special character",
          validated: false,
          image: this.validationImages?.sun,
        },
      ],
    };
  },
  mounted() {
    // if (this.type === "date" && this.modelValue) {
    //   this.modelValue = moment(this.modelValue).format("YYYY-MM-DD");
    // }

    this.setInputStyle();
  },
  watch: {
    status() {
      this.setInputStyle();
    },
    modelValue(value) {
      if (this.isEmail()) {
        this.emailValidation(value);
      } else {
        this.checkValidation();
      }
    },
  },
  computed: {
    // formattedModelValue() {
    //   return this.modelValue
    //     ? this.modelValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    //     : "";
    // },

    minVal() {
      if (this.type === "date" && this.min) {
        return moment(this.min).format("YYYY-MM-DD");
      }
      return Number(this.min);
    },

    maxVal() {
      if (this.type === "date" && this.max) {
        return moment(this.max).format("YYYY-MM-DD");
      }
      return Number(this.max);
    },
  },
  methods: {
    isEmail() {
      return this.type === "email";
    },
    emailValidation(email) {
      if (!email) return;
      const isValid = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,9})+$/.test(
        email,
      );

      this.emailValError = isValid ? null : "invalid email";
    },

    checkValidation() {
      const hasNumber = /\d/;
      const hasUppercase = /[A-Z]/;
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/;
      const hasMinLength = /.{8,}/;

      this.validationRuleList[0].image = hasMinLength.test(this.modelValue)
        ? this.validationImages.check
        : this.validationImages.sun;
      this.validationRuleList[0].validated = hasMinLength.test(this.modelValue);

      this.validationRuleList[1].image = hasUppercase.test(this.modelValue)
        ? this.validationImages?.check
        : this.validationImages?.sun;
      this.validationRuleList[1].validated = hasUppercase.test(this.modelValue);

      this.validationRuleList[2].image = hasNumber.test(this.modelValue)
        ? this.validationImages?.check
        : this.validationImages?.sun;
      this.validationRuleList[2].validated = hasNumber.test(this.modelValue);

      this.validationRuleList[3].image = hasSpecialChar.test(this.modelValue)
        ? this.validationImages?.check
        : this.validationImages?.sun;
      this.validationRuleList[3].validated = hasSpecialChar.test(
        this.modelValue,
      );
    },
    updateValue(event) {
      let el = event.target;

      // Auto resize if textarea
      if (this.type === "textarea") {
        this.autoGrow(el);
      }

      let newValue =
        this.type == "number" ? Number(event.target.value) : event.target.value;

      this.$emit("update:modelValue", newValue);
      this.$emit("input", newValue);
    },

    // updateValue(event) {
    //   let newValue;
    //   if (this.type == "number") {
    //     newValue = Number(event.target.value);
    //   } else {
    //     newValue = event.target.value;
    //   }

    //   // .replace(/,/g, "");

    //   this.$emit("update:modelValue", newValue);
    //   this.$emit("input", newValue);
    // },
    clearInput() {
      this.$emit("update:modelValue", "");
      this.$emit("clear");
    },
    setInputStyle() {
      switch (this.status) {
        case "positive":
          this.statusStyle.wrapperStyle =
            "border-[0.5px] border-estationsGreen-500 bg-inputGrey-500 ";
          this.statusStyle.inputStyle =
            "placeholder:text-estationsGreen-500 bg-inputGrey-500";
          this.currentIcon = this.userIcons.positiveEye;
          break;

        case "negative":
          this.statusStyle.wrapperStyle =
            "border-[0.5px] border-[#D44333] bg-inputGrey-500 ";
          this.statusStyle.inputStyle =
            "placeholder:text-[#D44333] bg-inputGrey-500";
          this.currentIcon = this.userIcons.negativeEye;
          break;

        case "disabled":
          this.statusStyle.wrapperStyle = "bg-inputGrey-300 ";
          this.statusStyle.inputStyle =
            "placeholder:text-inputGrey-600 bg-inputGrey-500 cursor-not-allowed";
          break;

        case "active":
          this.statusStyle.wrapperStyle = "bg-inputGrey-500 ";
          this.statusStyle.inputStyle =
            "placeholder:text-inputGrey-900 bg-inputGrey-500";
          this.currentIcon = this.userIcons.eye;
          break;

        case "dropdownSearch":
          this.statusStyle.wrapperStyle =
            "border border-[0.5px] border-inputGrey-500 bg-[#fff] ";
          this.statusStyle.inputStyle =
            "placeholder:text-inputGrey-900 bg-[#fff]";
          break;

        case "tableInput":
          this.statusStyle.wrapperStyle = "bg-inputGrey-500";
          this.statusStyle.inputStyle =
            "placeholder:text-inputGrey-900 bg-inputGrey-500";
          this.currentIcon = this.userIcons.eye;
          break;
      }
    },
    toggleView() {
      this.currentIcon =
        this.inputType === "password"
          ? this.userIcons.crossedEye
          : this.userIcons.eye;
      this.inputType = this.inputType === "password" ? "text" : "password";
    },
    goToPage() {
      this.$router.push("/forgot-password");
    },
    autoGrow(el) {
      if (!this.autoResize) return;
      el.style.height = "auto";
      el.style.height = el.scrollHeight + "px";
    },
  },
};
</script>

<style>
/* Add your styles here */
</style>
